import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar, Plus, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const VacationRequest = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, balanceRes] = await Promise.all([
        axios.get('/vacation/my-requests'),
        axios.get('/vacation/balance')
      ]);
      
      setRequests(requestsRes.data);
      setBalance(balanceRes.data);
    } catch (error) {
      console.error('Error loading vacation data:', error);
      toast.error('Fehler beim Laden der Urlaubsdaten');
    } finally {
      setLoading(false);
    }
  };

  const calculateDays = () => {
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return days > 0 ? days : 0;
    }
    return 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const days = calculateDays();
    if (days <= 0) {
      toast.error('Ungültiger Datumsbereich');
      return;
    }

    if (balance && days > balance.remaining_days) {
      toast.error(`Nicht genug Urlaubstage verfügbar (Verfügbar: ${balance.remaining_days})`);
      return;
    }

    try {
      setLoading(true);
      await axios.post('/vacation/request', formData);
      
      toast.success('Urlaubsantrag erfolgreich eingereicht');
      setShowForm(false);
      setFormData({ start_date: '', end_date: '', reason: '' });
      loadData();
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Fehler beim Einreichen');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: Clock, color: 'bg-yellow-900/30 text-yellow-400 border-yellow-700', label: 'Ausstehend' },
      approved: { icon: CheckCircle, color: 'bg-green-900/30 text-green-400 border-green-700', label: 'Genehmigt' },
      rejected: { icon: XCircle, color: 'bg-red-900/30 text-red-400 border-red-700', label: 'Abgelehnt' }
    };
    const { icon: Icon, color, label } = config[status] || config.pending;
    return (
      <Badge className={`${color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-white">Urlaubsverwaltung</h1>
          <p className="text-zinc-400 mt-2">Urlaubsanträge stellen und verwalten</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="lg">
          {showForm ? 'Abbrechen' : <><Plus className="mr-2 h-5 w-5" />Neuer Antrag</>}
        </Button>
      </div>

      {/* Balance Card */}
      {balance && (
        <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-700">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-sm text-zinc-400 mb-1">Jahresurlaub {balance.year}</p>
                <p className="text-3xl font-bold text-white">{balance.total_days}</p>
                <p className="text-xs text-zinc-500">Tage</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-zinc-400 mb-1">Bereits genommen</p>
                <p className="text-3xl font-bold text-white">{balance.used_days}</p>
                <p className="text-xs text-zinc-500">Tage</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-zinc-400 mb-1">Noch verfügbar</p>
                <p className="text-3xl font-bold text-green-400">{balance.remaining_days}</p>
                <p className="text-xs text-zinc-500">Tage</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-zinc-400 mb-1">In Bearbeitung</p>
                <p className="text-3xl font-bold text-yellow-400">{balance.pending_days}</p>
                <p className="text-xs text-zinc-500">Tage</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Request Form */}
      {showForm && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Neuer Urlaubsantrag</CardTitle>
            <CardDescription>Wählen Sie Ihre gewünschten Urlaubstage</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Beginn *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">Ende *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    required
                  />
                </div>
              </div>

              {formData.start_date && formData.end_date && calculateDays() > 0 && (
                <div className="p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <p className="text-blue-400 font-semibold">
                    📅 {calculateDays()} Urlaubstag(e) werden beantragt
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="reason">Grund (optional)</Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="z.B. Familienurlaub, Städtereise..."
                  className="bg-zinc-950 border-zinc-800 text-white"
                  rows={3}
                />
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Calendar className="mr-2 h-5 w-5" />
                    Antrag einreichen
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Requests List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Meine Urlaubsanträge</CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400">Noch keine Urlaubsanträge</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map(request => (
                <div
                  key={request.request_id}
                  className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <Calendar className="h-8 w-8 text-blue-400" />
                    <div className="flex-1">
                      <p className="font-semibold text-white">
                        {new Date(request.start_date).toLocaleDateString('de-DE')} - {new Date(request.end_date).toLocaleDateString('de-DE')}
                      </p>
                      <p className="text-sm text-zinc-400">
                        {request.days_requested} Tag(e)
                        {request.reason && ` • ${request.reason}`}
                      </p>
                      {request.rejection_reason && (
                        <p className="text-sm text-red-400 mt-1">
                          Grund: {request.rejection_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(request.status)}
                    <p className="text-xs text-zinc-500">
                      {new Date(request.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VacationRequest;
